/**
 * Gift Tier System with Mobile Slider
 */

class GiftTierSystem {
  constructor() {
    this.container = document.querySelector('[data-gift-system]');
    if (!this.container) {
      return;
    }

    this.config = this.loadConfig();
    if (!this.config.enabled) {
      return;
    }

    this.progressBar = document.querySelector('[data-progress-fill]');
    this.progressText = document.querySelector('[data-progress-text]');
    this.tierCards = document.querySelectorAll('.gift-tier-card');
    
    this.currentCart = null;
    this.eligibleSubtotal = 0;
    this.currentTierIndex = -1;
    this.previousTierIndex = -1;
    this.selectedGift = null;
    this.sortedTiers = [...this.config.tiers].sort((a, b) => a.threshold - b.threshold);
    
    // Slider tracking
    this.giftSliders = {};
    this.isDrawer = this.container.classList.contains('drawer');
    this.isMobile = window.innerWidth <= 768;

    // Register globally for modal access
    window.__giftTierSystemInstance = this;

    this.init();
  }

  loadConfig() {
    const configEl = document.querySelector('[data-gift-config]');
    if (!configEl) return { enabled: false };
    
    try {
      return JSON.parse(configEl.textContent);
    } catch (e) {
      return { enabled: false };
    }
  }

  init() {
    this.attachEventListeners();
    this.fetchCart();
    this.initSliders();
    this.handleResize();
    
    // Listen to cart events
    document.addEventListener('cart:updated', () => this.fetchCart());
    document.addEventListener('cart:refresh', () => this.fetchCart());
    document.addEventListener('cart:build', () => this.fetchCart());
  }

  attachEventListeners() {
    // Use event delegation for gift selection buttons
    document.addEventListener('click', (e) => {
      const selectButton = e.target.closest('[data-select-gift]');
      if (selectButton) {
        e.preventDefault();
        e.stopPropagation();
        this.handleGiftSelection(e);
      }
    });

    // Listen for cart quantity changes
    if (window.MutationObserver) {
      const cartObserver = new MutationObserver(() => {
        this.fetchCart();
      });
      
      const cartElement = document.querySelector('[data-cart-contents]') || 
                          document.querySelector('.cart-drawer') ||
                          document.querySelector('#cart-drawer');
      
      if (cartElement) {
        cartObserver.observe(cartElement, { 
          childList: true, 
          subtree: true 
        });
      }
    }
  }

  handleResize() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth <= 768;
        
        if (wasMobile !== this.isMobile) {
          if (this.isMobile) {
            this.initSliders();
          } else {
            this.destroySliders();
          }
        }
      }, 250);
    });
  }

  initSliders() {
    if (!this.isDrawer && !this.isMobile) {
      return;
    }

    const giftSliders = document.querySelectorAll('.gift-slider');
    
    giftSliders.forEach(slider => {
      const sliderId = slider.id;
      
      if (this.giftSliders[sliderId] || slider.classList.contains('flickity-enabled')) {
        return;
      }
      
      const giftOptions = slider.querySelectorAll('.gift-option');
      if (giftOptions.length > 0) {
        this.initSlider(slider, sliderId);
      }
    });
  }

  destroySliders() {
    Object.keys(this.giftSliders).forEach(sliderId => {
      const sliderInstance = this.giftSliders[sliderId];
      
      if (sliderInstance) {
        if (sliderInstance.flickity && typeof sliderInstance.flickity.destroy === 'function') {
          sliderInstance.flickity.destroy();
        }
        else if (typeof sliderInstance.destroy === 'function') {
          sliderInstance.destroy();
        }
        
        delete this.giftSliders[sliderId];
      }
    });
  }

  reinitSliders() {
    if (!this.isDrawer && !this.isMobile) {
      return;
    }

    if (this.sliderReinitTimer) {
      clearTimeout(this.sliderReinitTimer);
    }

    this.sliderReinitTimer = setTimeout(() => {
      const giftSliders = document.querySelectorAll('.gift-slider');
      
      giftSliders.forEach(slider => {
        const sliderId = slider.id;
        const giftOptions = slider.querySelectorAll('.gift-option');
        
        if (giftOptions.length === 0) {
          if (this.giftSliders[sliderId]) {
            this.destroySlider(sliderId);
          }
          return;
        }
        
        if (this.giftSliders[sliderId] && slider.classList.contains('flickity-enabled')) {
          const sliderInstance = this.giftSliders[sliderId];
          if (sliderInstance.flickity && typeof sliderInstance.flickity.resize === 'function') {
            sliderInstance.flickity.resize();
          } else if (typeof sliderInstance.resize === 'function') {
            sliderInstance.resize();
          }
          return;
        }
        
        if (!this.giftSliders[sliderId]) {
          this.initSlider(slider, sliderId);
        }
      });
      
      this.sliderReinitTimer = null;
    }, 500);
  }

  initSlider(slider, sliderId) {
    if (window.theme && window.theme.Slideshow) {
      this.giftSliders[sliderId] = new theme.Slideshow(slider, {
        cellAlign: 'left',
        contain: true,
        pageDots: true,
        prevNextButtons: false,
        wrapAround: false,
        draggable: true,
        freeScroll: false,
        groupCells: true,
        adaptiveHeight: false
      });
    } else if (window.Flickity) {
      this.giftSliders[sliderId] = new Flickity(slider, {
        cellAlign: 'left',
        contain: true,
        pageDots: true,
        prevNextButtons: false,
        wrapAround: false,
        draggable: true,
        freeScroll: false,
        groupCells: true
      });
    }
  }

  destroySlider(sliderId) {
    const sliderInstance = this.giftSliders[sliderId];
    
    if (sliderInstance) {
      if (sliderInstance.flickity && typeof sliderInstance.flickity.destroy === 'function') {
        sliderInstance.flickity.destroy();
      }
      else if (typeof sliderInstance.destroy === 'function') {
        sliderInstance.destroy();
      }
      
      delete this.giftSliders[sliderId];
    }
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      this.currentCart = await response.json();
      this.calculateEligibility();
      await this.updateUI();
    } catch (error) {
      // Error fetching cart
    }
  }

  calculateEligibility() {
    if (!this.currentCart) return;

    const excludedProductIds = this.config.excludedProducts
      .map(id => parseInt(id.trim()))
      .filter(id => !isNaN(id));

    this.eligibleSubtotal = 0;
    
    this.currentCart.items.forEach(item => {
      const isGift = this.isGiftItem(item);
      const isExcluded = excludedProductIds.includes(item.product_id);
      const itemTotal = (item.original_price * item.quantity) / 100;
      
      if (isGift || isExcluded) {
        return;
      }
      
      this.eligibleSubtotal += itemTotal;
    });

    this.previousTierIndex = this.currentTierIndex;
    this.currentTierIndex = -1;
    
    for (let i = 0; i < this.sortedTiers.length; i++) {
      const tier = this.sortedTiers[i];
      if (this.eligibleSubtotal >= tier.threshold) {
        this.currentTierIndex = i;
      } else {
        break;
      }
    }
  }

  isGiftItem(item) {
    if (item.properties) {
      if (item.properties._gift_item === 'true' || item.properties._gift_item === true) {
        return true;
      }
    }
    
    if (item.line_item_properties) {
      const giftProp = item.line_item_properties.find(prop => prop.name === '_gift_item');
      if (giftProp && (giftProp.value === 'true' || giftProp.value === true)) {
        return true;
      }
    }
    return false;
  }

  getAllGiftVariantIds() {
    const giftOptions = document.querySelectorAll('[data-gift-option], [data-gift-item]');
    const variantIds = [];
    
    giftOptions.forEach(option => {
      let variantId;
      if (option.hasAttribute('data-gift-item')) {
        const hiddenDiv = option.querySelector('[data-variant-id]');
        variantId = hiddenDiv ? parseInt(hiddenDiv.dataset.variantId) : null;
      } else {
        variantId = parseInt(option.dataset.variantId);
      }
      
      if (variantId && !variantIds.includes(variantId)) {
        variantIds.push(variantId);
      }
    });
    
    return variantIds;
  }

  async updateUI() {
    this.updateProgressBar();
    await this.updateVisibleTiers();
    await this.handleTierTransition();
    this.updateTierCards();
    await this.checkAutoGifts();

    this.reinitSliders();
  }

  updateProgressBar() {
    if (!this.progressBar || !this.progressText) return;

    if (this.sortedTiers.length === 0) {
      this.progressBar.style.width = '0%';
      this.progressText.textContent = 'No tiers configured';
      return;
    }

    const nextTierIndex = this.currentTierIndex + 1;
    
    if (nextTierIndex >= this.sortedTiers.length) {
      this.progressBar.style.width = '100%';
      this.progressText.textContent = '🎉 Maximum tier reached!';
      return;
    }

    const nextTier = this.sortedTiers[nextTierIndex];
    const previousThreshold = this.currentTierIndex >= 0 
      ? this.sortedTiers[this.currentTierIndex].threshold 
      : 0;
    
    const tierRange = nextTier.threshold - previousThreshold;
    const progress = ((this.eligibleSubtotal - previousThreshold) / tierRange) * 100;
    const clampedProgress = Math.max(0, Math.min(100, progress));
    
    this.progressBar.style.width = clampedProgress + '%';

    const remaining = Math.max(0, nextTier.threshold - this.eligibleSubtotal);
    
    if (remaining > 0) {
      const formattedAmount = this.formatMoney(remaining);
      const text = this.config.progressBarText
        .replace('{amount}', formattedAmount)
        .replace('{tier}', nextTier.label);
      this.progressText.textContent = text;
    } else {
      const text = this.config.unlockedText
        .replace('{tier}', nextTier.label);
      this.progressText.textContent = text;
    }
  }

  async updateVisibleTiers() {
    const tiersToShow = [];
    
    if (this.currentTierIndex >= 0) {
      if (this.currentTierIndex === this.sortedTiers.length - 1) {
        tiersToShow.push(this.currentTierIndex);
      } else {
        tiersToShow.push(this.currentTierIndex);
        if (this.currentTierIndex + 1 < this.sortedTiers.length) {
          tiersToShow.push(this.currentTierIndex + 1);
        }
      }
    } else {
      if (this.sortedTiers.length > 0) tiersToShow.push(0);
      if (this.sortedTiers.length > 1) tiersToShow.push(1);
    }

    this.tierCards.forEach(card => {
      card.style.display = 'none';
    });

    tiersToShow.forEach(index => {
      const tier = this.sortedTiers[index];
      const card = this.findTierCard(tier.tier);
      if (card) {
        card.style.display = 'block';
      }
    });
  }

  findTierCard(tierNumber) {
    return Array.from(this.tierCards).find(card => 
      parseInt(card.dataset.tier) === tierNumber
    );
  }

  async handleTierTransition() {
    let giftsRemoved = false;
    
    if (this.previousTierIndex !== this.currentTierIndex && 
        this.previousTierIndex >= 0 && 
        this.currentTierIndex > this.previousTierIndex) {
      
      const previousTier = this.sortedTiers[this.previousTierIndex];
      await this.removeTierGift(previousTier);
      giftsRemoved = true;
    }
    
    if (this.previousTierIndex !== this.currentTierIndex && 
        this.previousTierIndex >= 0 && 
        this.currentTierIndex < this.previousTierIndex) {
      
      for (let i = this.previousTierIndex; i > this.currentTierIndex; i--) {
        const lockedTier = this.sortedTiers[i];
        await this.removeTierGift(lockedTier);
      }
      giftsRemoved = true;
    }
    
    if (this.currentTierIndex === -1 && this.previousTierIndex >= 0) {
      for (let i = 0; i <= this.previousTierIndex; i++) {
        const tier = this.sortedTiers[i];
        await this.removeTierGift(tier);
      }
      giftsRemoved = true;
    }
    
    if (giftsRemoved) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        const response = await fetch('/cart.js');
        const cartData = await response.json();
        cartDrawer.renderContents(cartData);
      }
      
      if (window.theme && window.theme.Cart && typeof window.theme.Cart.get === 'function') {
        window.theme.Cart.get();
      }
      
      await new Promise(resolve => setTimeout(resolve, 600));
    }
  }

  async removeTierGift(tier) {
    const giftItems = this.currentCart.items.filter(item => {
      if (!this.isGiftItem(item)) return false;
      if (item.properties && item.properties._gift_tier === tier.label) {
        return true;
      }
      return false;
    });

    for (const giftItem of giftItems) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: giftItem.key,
            quantity: 0
          })
        });
      } catch (error) {
        // Error removing gift
      }
    }
    
    await this.fetchCart();
    document.dispatchEvent(new CustomEvent('cart:build'));
    await new Promise(resolve => setTimeout(resolve, 300));
  }

  updateTierCards() {
    this.tierCards.forEach(card => {
      const tierNum = parseInt(card.dataset.tier);
      const threshold = parseFloat(card.dataset.threshold);
      const mode = card.dataset.mode;
      const statusEl = card.querySelector('[data-tier-status]');
      
      const isUnlocked = this.eligibleSubtotal >= threshold;
      
      card.classList.toggle('unlocked', isUnlocked);
      card.classList.toggle('locked', !isUnlocked);
      
      if (statusEl) {
        statusEl.className = 'tier-status ' + (isUnlocked ? 'unlocked' : 'locked');
        statusEl.textContent = isUnlocked ? '✓ Unlocked' : '🔒 Locked';
      }
      
      const selectButtons = card.querySelectorAll('[data-select-gift]');
      selectButtons.forEach(btn => {
        if (!isUnlocked) {
          btn.disabled = true;
          btn.classList.add('locked-btn');
        } else {
          const isSelected = btn.classList.contains('selected');
          btn.disabled = isSelected;
          btn.classList.remove('locked-btn');
        }
      });
      
      if (isUnlocked && mode === 'choice') {
        this.checkSelectedGift(card);
      }
    });
  }

  checkSelectedGift(tierCard) {
    const giftOptions = tierCard.querySelectorAll('[data-gift-option]');
    
    giftOptions.forEach(option => {
      const variantId = parseInt(option.dataset.variantId);
      const quantity = parseInt(option.dataset.quantity) || 1;
      
      const hasGift = this.currentCart.items.some(item => 
        item.variant_id === variantId && 
        this.isGiftItem(item) &&
        item.quantity >= quantity &&
        (item.properties._gift_item === 'true' || item.properties._gift_item === true)
      );
      
      const btn = option.querySelector('[data-select-gift]');
      if (btn) {
        if (hasGift) {
          btn.textContent = '✓ Selected';
          btn.classList.add('selected');
          btn.disabled = true;
          option.classList.add('selected');
        } else {
          btn.textContent = 'Select Gift';
          btn.classList.remove('selected');
          btn.disabled = false;
          option.classList.remove('selected');
        }
      }
    });
  }

  async checkAutoGifts() {
    const visibleAutoTiers = Array.from(this.tierCards)
      .filter(card => card.style.display !== 'none' && card.dataset.mode === 'automatic');
    
    for (const tierCard of visibleAutoTiers) {
      const threshold = parseFloat(tierCard.dataset.threshold);
      const isUnlocked = this.eligibleSubtotal >= threshold;
      
      if (isUnlocked) {
        await this.ensureAutoGift(tierCard);
      }
    }
  }

  async ensureAutoGift(tierCard) {
    const tierLabel = tierCard.dataset.label;
    const giftItem = tierCard.querySelector('[data-gift-item]');
    
    if (!giftItem) return;
    
    const hiddenDiv = giftItem.querySelector('[data-variant-id]');
    if (!hiddenDiv) return;
    
    const variantId = parseInt(hiddenDiv.dataset.variantId);
    const quantity = parseInt(hiddenDiv.dataset.quantity) || 1;
    const productTitle = hiddenDiv.dataset.productTitle;
    const isAvailable = hiddenDiv.dataset.available === 'true';
    
    if (!variantId || !isAvailable) return;
    
    const hasGift = this.currentCart.items.some(item => 
      this.isGiftItem(item) && 
      item.properties && 
      item.properties._gift_tier === tierLabel &&
      item.variant_id === variantId &&
      item.quantity >= quantity
    );
    
    if (hasGift) {
      return;
    }
    
    try {
      await this.addGiftToCart(variantId, productTitle, tierLabel, quantity);
      this.showNotification(`${productTitle} automatically added!`);
    } catch (error) {
      // Error auto-adding gift
    }
  }

  async handleGiftSelection(e) {
    const btn = e.target.closest('[data-select-gift]');
    const giftOption = btn ? btn.closest('[data-gift-option]') : null;
    
    if (!giftOption) {
      return;
    }
    
    const tierCard = giftOption.closest('.gift-tier-card');
    const tierLabel = tierCard ? tierCard.dataset.label : 'unknown';
    const threshold = tierCard ? parseFloat(tierCard.dataset.threshold) : 0;
    
    if (this.eligibleSubtotal < threshold) {
      alert(`This tier is locked. Spend ${this.formatMoney(threshold - this.eligibleSubtotal)} more to unlock it.`);
      return;
    }
    
    const variantId = parseInt(giftOption.dataset.variantId);
    const quantity = parseInt(giftOption.dataset.quantity) || 1;
    const productTitle = giftOption.dataset.productTitle;
    const isAvailable = giftOption.dataset.available === 'true';
    
    if (!isAvailable) {
      alert('This gift is currently out of stock');
      return;
    }
    
    if (!variantId) {
      alert('Error: No variant ID found for this gift');
      return;
    }
    
    btn.disabled = true;
    btn.textContent = 'Adding...';
    
    try {
      await this.removeCurrentTierGift(tierLabel);
      await this.addGiftToCart(variantId, productTitle, tierLabel, quantity);
      this.showNotification(this.config.giftAddedText);
      await this.fetchCart();
      document.dispatchEvent(new CustomEvent('cart:build'));
      this.trackGiftSelection(productTitle, variantId, tierLabel, quantity);
      
    } catch (error) {
      alert('Error adding gift to cart. Please try again.');
      btn.disabled = false;
      btn.textContent = 'Select Gift';
    }
  }

  async removeCurrentTierGift(tierLabel) {
    const giftItems = this.currentCart.items.filter(item => {
      if (!this.isGiftItem(item)) return false;
      if (item.properties && item.properties._gift_tier === tierLabel) {
        return true;
      }
      return false;
    });

    for (const giftItem of giftItems) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0
        })
      });
    }
    
    await new Promise(resolve => setTimeout(resolve, 300));
  }

  async addGiftToCart(variantId, productTitle, tierLabel, quantity = 1) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: quantity,
          properties: {
            '_gift_item': 'true',
            '_gift_tier': tierLabel,
            '_gift_sku': productTitle
          }
        }]
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add gift to cart');
    }
    
    return await response.json();
  }

  showNotification(message) {
    const notification = document.createElement('div');
    notification.className = 'gift-notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #2ecc71;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  trackGiftSelection(productTitle, variantId, tierLabel, quantity) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'gift_selected',
        gift_name: productTitle,
        gift_variant_id: variantId,
        gift_quantity: quantity,
        tier: tierLabel,
        cart_total: this.eligibleSubtotal
      });
    }
    
    if (window._learnq) {
      window._learnq.push(['track', 'Gift Selected', {
        gift_name: productTitle,
        gift_variant_id: variantId,
        gift_quantity: quantity,
        tier: tierLabel,
        cart_total: this.eligibleSubtotal
      }]);
    }
  }

  formatMoney(amount) {
    return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

/**
 * Gift Warning Modal Handler
 */
class GiftWarningModal {
  constructor() {
    console.log('🎁 GiftWarningModal constructor called');
    
    this.modal = document.getElementById('gift-warning-modal');
    
    if (!this.modal) {
      console.warn('⚠️ Gift warning modal element not found in DOM');
      return;
    }
    
    console.log('✅ Modal element found:', this.modal);
    
    this.overlay = this.modal.querySelector('.gift-warning-overlay');
    this.closeBtn = this.modal.querySelector('.gift-warning-close');
    this.selectBtn = this.modal.querySelector('.gift-warning-select');
    this.continueBtn = this.modal.querySelector('.gift-warning-continue');
    
    // Wait for gift system to be available
    this.waitForGiftSystem();
  }
  
  waitForGiftSystem() {
    console.log('⏳ Waiting for gift system...');
    
    const checkSystem = () => {
      if (window.__giftTierSystemInstance) {
        console.log('✅ Gift system found!', window.__giftTierSystemInstance);
        this.giftSystem = window.__giftTierSystemInstance;
        this.init();
      } else {
        console.log('⏳ Gift system not ready yet, checking again...');
        setTimeout(checkSystem, 100);
      }
    };
    
    checkSystem();
  }
  
  init() {
    console.log('🔧 Initializing Gift Warning Modal');
    
    // Intercept checkout buttons
    this.interceptCheckoutButtons();
    
    // Modal close handlers
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }
    
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    
    // Select gift button
    if (this.selectBtn) {
      this.selectBtn.addEventListener('click', () => {
        this.close();
        this.scrollToGifts();
      });
    }
    
    // Continue button
    if (this.continueBtn) {
      this.continueBtn.addEventListener('click', () => {
        this.close();
        this.proceedToCheckout();
      });
    }
    
    // ESC key closes modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
    
    console.log('✅ Gift Warning Modal fully initialized');
  }
  
  interceptCheckoutButtons() {
    console.log('🔧 Setting up checkout button interception');
    
    // Use event delegation on the entire document
    document.addEventListener('click', (e) => {
      const target = e.target;
      
      // Check if clicked element is a checkout button
      const isCheckoutButton = 
        target.getAttribute('name') === 'waio_addToCart' ||
        target.getAttribute('name') === 'checkout' ||
        target.classList.contains('cart__checkout') ||
        target.closest('[name="waio_addToCart"]') ||
        target.closest('[name="checkout"]') ||
        target.closest('.cart__checkout');
      
      if (isCheckoutButton) {
        console.log('🛒 Checkout button clicked:', target);
        console.log('🔍 Checking if should show warning...');
        
        if (this.shouldShowWarning()) {
          console.log('⚠️ SHOWING WARNING MODAL');
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.open();
          return false;
        } else {
          console.log('✅ No warning needed, proceeding with checkout');
        }
      }
    }, true); // Use capture phase
    
    console.log('✅ Checkout button interception setup complete');
  }
  
  shouldShowWarning() {
    console.log('🔍 shouldShowWarning called');
    
    if (!this.giftSystem) {
      console.log('❌ No gift system');
      return false;
    }
    
    if (!this.giftSystem.currentCart) {
      console.log('❌ No current cart');
      return false;
    }
    
    console.log('📊 Eligible subtotal:', this.giftSystem.eligibleSubtotal);
    console.log('📊 Sorted tiers:', this.giftSystem.sortedTiers);
    
    const unlockedTiers = this.giftSystem.sortedTiers.filter(tier => 
      this.giftSystem.eligibleSubtotal >= tier.threshold
    );
    
    console.log('🔓 Unlocked tiers:', unlockedTiers);
    
    if (unlockedTiers.length === 0) {
      console.log('❌ No unlocked tiers');
      return false;
    }
    
    const choiceTiers = unlockedTiers.filter(tier => tier.mode === 'choice');
    
    console.log('🎯 Choice tiers:', choiceTiers);
    
    for (const tier of choiceTiers) {
      const hasGiftFromTier = this.giftSystem.currentCart.items.some(item =>
        this.giftSystem.isGiftItem(item) &&
        item.properties &&
        item.properties._gift_tier === tier.label
      );
      
      console.log(`Tier "${tier.label}" has gift:`, hasGiftFromTier);
      
      if (!hasGiftFromTier) {
        console.log(`✅ SHOULD SHOW WARNING - No gift selected for tier: ${tier.label}`);
        return true;
      }
    }
    
    console.log('❌ All choice tiers have gifts selected');
    return false;
  }
  
  open() {
    console.log('🚪 Opening modal');
    if (this.modal) {
      this.modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      
      if (window.theme && window.theme.a11y) {
        theme.a11y.trapFocus({
          container: this.modal,
          namespace: 'gift_warning_modal'
        });
      }
      
      console.log('✅ Modal opened');
    }
  }
  
  close() {
    console.log('🚪 Closing modal');
    if (this.modal) {
      this.modal.style.display = 'none';
      document.body.style.overflow = '';
      
      if (window.theme && window.theme.a11y) {
        theme.a11y.removeTrapFocus({
          container: this.modal,
          namespace: 'gift_warning_modal'
        });
      }
      
      console.log('✅ Modal closed');
    }
  }
  
  isOpen() {
    return this.modal && this.modal.style.display === 'flex';
  }
  
  scrollToGifts() {
    const giftContainer = document.querySelector('[data-gift-system]');
    
    if (giftContainer) {
      giftContainer.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
      
      setTimeout(() => {
        const unlockedCards = giftContainer.querySelectorAll('.gift-tier-card.unlocked');
        unlockedCards.forEach(card => {
          card.style.animation = 'pulse 0.5s ease';
          setTimeout(() => {
            card.style.animation = '';
          }, 500);
        });
      }, 500);
    }
  }
  
  proceedToCheckout() {
    console.log('➡️ Proceeding to checkout');
    window.location.href = '/checkout';
  }
}

// Add pulse animation CSS
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); box-shadow: 0 8px 24px rgba(46, 204, 113, 0.3); }
  }
`;
document.head.appendChild(style);

// Initialize when DOM is ready
console.log('📦 Gift Tier System script loaded');

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('🚀 DOM Content Loaded - Initializing systems');
    new GiftTierSystem();
    new GiftWarningModal();
  });
} else {
  console.log('🚀 DOM already loaded - Initializing systems immediately');
  new GiftTierSystem();
  new GiftWarningModal();
}